import { ApicallOption, ApicallService } from "@core-services/apicall.service";
import { Injectable } from "@angular/core";
import {
	FiatTransaction,
	FiatTransactionDtoAdapter
} from "@modules/+new/transactions/domain/entities/fiat-transaction.model";
import { EMPTY, Observable, of } from "rxjs";
import { FiatTransactionDto } from "@modules/+new/transactions/infrastructure/dto/fiat-transaction.dto";
import { BasicApiObject, Helper, parseApiResponseObject, parseApiResponseObject2 } from "@helpers/functions";
import { catchError, map, switchMap, take, tap } from "rxjs/operators";
import { CreateDepositTransactionRequest } from "@modules/+new/transactions/domain/entities/create-deposit-transaction-request.model";
import { ErrorClass, UnauthorizedError } from "@models";
import { InfoService } from "@core-services/info.service";
import { TranslationService } from "@core-services/translation.service";
import { ConfirmDepositTransactionRequest } from "@modules/+new/transactions/domain/entities/confirm-deposit-transaction-request.model";
import { ApproveDepositTransactionRequest } from "@modules/+new/transactions/domain/entities/approve-deposit-transaction-request.model";
import { FiatTransactionPaymentDto } from "@modules/+new/transactions/infrastructure/dto/fiat-transaction-payment.dto";
import {
	FiatTransactionPayment,
	FiatTransactionPaymentDtoAdapter
} from "@modules/+new/transactions/domain/entities/fiat-transaction-payment.model";
import { FiatTransactionDepositResponse } from "@modules/shared/domain/entities/fiat-transaction-deposit-response.model";
import { StatsigClientService, FEAT_GATES } from "@core-services/statsig/statsig.service";

@Injectable({
	providedIn: "any"
})
export class TransactionsRemote {
	public constructor(
		private readonly apiService: ApicallService,
		private readonly infoService: InfoService,
		private readonly translationService: TranslationService,
		private readonly statsigClientService: StatsigClientService
	) {}

	public createDepositTransaction$(request: CreateDepositTransactionRequest): Observable<FiatTransaction> {
		return this.statsigClientService.isFeatureActive(FEAT_GATES.DEPOSIT_MIGRATION_ENABLED).pipe(
			take(1),
			catchError(() => of(false)),
			switchMap((isDepositMigrationActive: boolean) => {
				const option = new ApicallOption(null, request.requestBody);
				option.apiVersion = "v2";
				const reqUrl = isDepositMigrationActive
					? `fiat/deposit`
					: `fiatwallet/${request.walletGuid}/deposit` +
						(request.requestBody.trade_flow
							? `?trade_flow=${request.requestBody.trade_flow}
					&asset_id=${request.requestBody.asset_id}&asset_wallet_id=${request.requestBody.asset_wallet_id}`
							: "");

				return this.apiService.post(reqUrl, option).pipe(
					catchError((error: unknown) => Helper.handleError$(error)),
					map((apiObject: BasicApiObject) => parseApiResponseObject<FiatTransactionDto>(apiObject)),
					map((dto: FiatTransactionDto) => new FiatTransactionDtoAdapter().adapt(dto))
				);
			})
		);
	}

	public confirmDepositTransaction$({
		walletGuid,
		transactionId,
		requestBody
	}: ConfirmDepositTransactionRequest): Observable<FiatTransaction> {
		const option = new ApicallOption(null, requestBody);
		option.ignore401 = true; // TODO verify (old logic)

		return this.apiService.post(`fiatwallet/${walletGuid}/deposit/${transactionId}/confirm`, option).pipe(
			catchError((error: unknown) => this.handleConfirmDepositTransactionError$(error)),
			map((apiObject: BasicApiObject) => parseApiResponseObject<FiatTransactionDto>(apiObject)),
			map((dto: FiatTransactionDto) => new FiatTransactionDtoAdapter().adapt(dto))
		);
	}

	public getTransactionPaymentByWallet$(walletId: string, transactionId: string): Observable<FiatTransactionPayment> {
		return this.statsigClientService.isFeatureActive(FEAT_GATES.DEPOSIT_MIGRATION_ENABLED).pipe(
			take(1),
			catchError(() => of(false)),
			switchMap((isDepositMigrationActive: boolean) => {
				const option: ApicallOption = new ApicallOption();
				let url: string = `fiatwallet/${walletId}/deposit/${transactionId}/pay`;

				if (isDepositMigrationActive) {
					url = `deposit/${transactionId}/pay`;
					option.apiVersion = "v2";
				}

				return this.apiService.get(url, option).pipe(
					catchError((error: unknown) => Helper.handleError$(error)),
					map((apiObject: BasicApiObject) => parseApiResponseObject<FiatTransactionPaymentDto>(apiObject)),
					map((dto: FiatTransactionPaymentDto) => new FiatTransactionPaymentDtoAdapter().adapt(dto))
				);
			})
		);
	}

	private handleConfirmDepositTransactionError$(error: unknown): Observable<never> {
		if (!(error instanceof ErrorClass)) {
			console.error("Err: ", error);
			return EMPTY;
		}

		if (error instanceof UnauthorizedError) {
			this.infoService.setError(this.translationService.translate("info_text_verify_pin_invalid_pin"));
			return EMPTY;
		}

		this.infoService.setError(error);
		return EMPTY;
	}

	public approveDepositTransaction$({
		walletGuid,
		transactionId,
		type
	}: ApproveDepositTransactionRequest): Observable<void> {
		return this.apiService.get(`fiatwallet/${walletGuid}/deposit/${transactionId}/approve/${type}`).pipe(
			catchError((error: unknown) => Helper.handleError$(error)),
			tap(() => this.infoService.setSuccess(this.translationService.translate("sms_code_was_sent")))
		);
	}

	public getDepositTransactionResponse$(fiatTransactionPid: string): Observable<FiatTransactionDepositResponse> {
		return this.apiService.get(`payments/fiat-wallet-transaction/${fiatTransactionPid}`).pipe(
			map((res) => parseApiResponseObject2<FiatTransactionDepositResponse>(res)),
			catchError((res) => {
				console.error("Err: ", res);
				return EMPTY;
			})
		);
	}
}
